import { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Copy, Terminal, Code } from "lucide-react";
import { toast } from "sonner";

const SNIPPETS = {
  curl: `curl -X POST https://api.thegoodguys.app/api/validate/phone \\
  -H "Authorization: Bearer gg_live_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"value":"+14155552671"}'`,
  javascript: `const r = await fetch("https://api.thegoodguys.app/api/validate/email", {
  method: "POST",
  headers: {
    "Authorization": "Bearer gg_live_YOUR_KEY",
    "Content-Type": "application/json"
  },
  body: JSON.stringify({ value: "hello@stripe.com" })
});
const data = await r.json();
console.log(data.result.status);`,
  python: `import requests

r = requests.post(
    "https://api.thegoodguys.app/api/validate/ip",
    headers={"Authorization": "Bearer gg_live_YOUR_KEY"},
    json={"value": "8.8.8.8"},
)
print(r.json()["result"]["risk_level"])`,
  go: `req, _ := http.NewRequest("POST", "https://api.thegoodguys.app/api/validate/phone",
    strings.NewReader(\`{"value":"+14155552671"}\`))
req.Header.Set("Authorization", "Bearer gg_live_YOUR_KEY")
req.Header.Set("Content-Type", "application/json")
resp, _ := http.DefaultClient.Do(req)`
};

const ENDPOINTS = [
  { method: "POST", path: "/api/validate/phone", desc: "HLR + carrier + spam lookup" },
  { method: "POST", path: "/api/validate/email", desc: "Syntax + MX + SMTP + disposable" },
  { method: "POST", path: "/api/validate/ip", desc: "Geo + Tor + VPN + proxy risk" },
  { method: "POST", path: "/api/validate/bulk", desc: "Batch up to 500 values per request" },
  { method: "POST", path: "/api/keys/generate", desc: "Create a new Bearer key" },
  { method: "GET",  path: "/api/lookups", desc: "Recent verification history" },
  { method: "GET",  path: "/api/stats", desc: "Usage stats by kind" },
];

export default function Docs() {
  const [lang, setLang] = useState("curl");

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Snippet copied"); };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-10">
        <span className="font-mono text-xs uppercase tracking-widest text-cyan-signal">API Reference</span>
        <h1 className="mt-2 text-4xl sm:text-5xl font-display font-bold tracking-tight">Developer Docs</h1>
        <p className="mt-3 text-slate-400 max-w-2xl">HTTPS. Bearer tokens. JSON in, JSON out. Every endpoint responds under 300ms.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-6">
            <h2 className="font-display text-xl font-semibold mb-3 flex items-center gap-2"><Terminal className="w-5 h-5 text-cyan-signal" /> Authentication</h2>
            <p className="text-sm text-slate-300 mb-3">All authenticated endpoints require a Bearer token. Get one from your <a href="/dashboard" className="text-cyan-signal hover:underline">Dashboard → API Keys</a>.</p>
            <pre className="bg-slate-950 border border-slate-800 rounded-lg p-4 text-xs font-mono text-cyan-200 overflow-x-auto">Authorization: Bearer gg_live_YOUR_KEY</pre>
          </section>

          <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-xl font-semibold flex items-center gap-2"><Code className="w-5 h-5 text-cyan-signal" /> Quickstart</h2>
              <Button size="sm" variant="ghost" onClick={() => copy(SNIPPETS[lang])} data-testid="btn-copy-snippet" className="text-slate-400 hover:text-white"><Copy className="w-4 h-4" /></Button>
            </div>
            <Tabs value={lang} onValueChange={setLang}>
              <TabsList className="bg-slate-950 border border-slate-800 gap-1 h-auto p-1">
                {["curl", "javascript", "python", "go"].map((l) => (
                  <TabsTrigger key={l} data-testid={`docs-tab-${l}`} value={l} className="font-mono text-xs uppercase data-[state=active]:bg-cyan-500/10 data-[state=active]:text-cyan-signal">{l}</TabsTrigger>
                ))}
              </TabsList>
              {Object.entries(SNIPPETS).map(([k, v]) => (
                <TabsContent key={k} value={k} className="mt-3">
                  <pre className="bg-slate-950 border border-slate-800 rounded-lg p-4 text-xs sm:text-sm font-mono text-cyan-200 overflow-x-auto whitespace-pre">{v}</pre>
                </TabsContent>
              ))}
            </Tabs>
          </section>

          <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-6">
            <h2 className="font-display text-xl font-semibold mb-4">Endpoints</h2>
            <div className="space-y-2">
              {ENDPOINTS.map((e) => (
                <div key={e.path} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 rounded-lg border border-slate-800 bg-slate-950/50 px-4 py-3">
                  <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-mono font-semibold w-fit ${
                    e.method === "GET" ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" : "bg-cyan-500/10 text-cyan-signal border border-cyan-500/30"
                  }`}>{e.method}</span>
                  <code className="font-mono text-sm text-slate-100 flex-1">{e.path}</code>
                  <span className="text-xs text-slate-500">{e.desc}</span>
                </div>
              ))}
            </div>
          </section>
        </div>

        <aside className="rounded-xl border border-slate-800 bg-slate-900/60 p-6 h-fit lg:sticky lg:top-24">
          <h3 className="font-display font-semibold text-lg mb-3">Response schema</h3>
          <pre className="bg-slate-950 border border-slate-800 rounded-lg p-3 text-[11px] font-mono text-cyan-200 overflow-x-auto leading-relaxed">
{`{
  "kind": "phone",
  "credits_used": 1,
  "result": {
    "e164": "+14155552671",
    "valid_format": true,
    "reachable": true,
    "carrier": "Verizon Wireless",
    "mcc": "310",
    "mnc": "004",
    "line_type": "mobile",
    "ported": false,
    "hlr_status": "active",
    "spam_score": 12,
    "spam_risk": "low",
    "dnc_registry": false
  }
}`}
          </pre>
          <h3 className="font-display font-semibold text-lg mt-6 mb-3">Rate limits</h3>
          <ul className="text-sm text-slate-300 space-y-1">
            <li>· 200 QPS default</li>
            <li>· Bulk up to 500 per call</li>
            <li>· Free tier: 10 req/min</li>
          </ul>
        </aside>
      </div>
    </div>
  );
}
